import { useEffect, useId, useState, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { ChevronRight, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NAV_ITEMS, type NavGroup } from './navItems'
import { InstallAppRow } from './InstallAppPrompt'
import { NavBadge } from './NavBadge'

const GROUPS: NavGroup[] = ['Everyday', 'Money', 'Plan & insights', 'Settings']

/** The phone's Menu: every section, grouped, with a search, and the account controls at the
 *  bottom — what the desktop sidebar holds, opened from the tab bar. */
export function MobileMenuSheet({
  onClose,
  footer,
  badges = {},
}: {
  onClose: () => void
  /** A count next to a section, keyed by its route — reminders due, next to Recurring. */
  badges?: Record<string, number>
  /** Account area: read-only badge, theme and the account menu. */
  footer: ReactNode
}) {
  const titleId = useId()
  const [query, setQuery] = useState('')

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const q = query.trim().toLowerCase()
  const matches = NAV_ITEMS.filter((item) => item.label.toLowerCase().includes(q))

  const link = (item: (typeof NAV_ITEMS)[number]) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={onClose}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-2 py-2.5 text-[15px] font-medium text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]',
          isActive && 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary)]'
        )
      }
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[var(--fl-primary-faint)] text-[var(--fl-primary)]">
        <item.Icon size={17} aria-hidden="true" />
      </span>
      <span className="flex-1">{item.label}</span>
      <NavBadge count={badges[item.to] ?? 0} />
      <ChevronRight size={16} aria-hidden="true" className="text-[var(--fl-text-3)]" />
    </NavLink>
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/40 lg:hidden"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[85vh] w-full flex-col rounded-t-[20px] bg-[var(--fl-surface)] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] motion-safe:animate-fade-slide-up"
      >
        <div
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-10 rounded-full bg-[var(--fl-border)]"
        />
        <h2 id={titleId} className="sr-only">
          Menu
        </h2>
        <div className="px-4 pt-3">
          <InstallAppRow className="mb-3" />
          <label className="flex h-10 items-center gap-2 rounded-xl border border-[var(--fl-border)] bg-[var(--fl-bg)] px-3 text-[var(--fl-text-3)] focus-within:border-[var(--fl-primary)]">
            <Search size={16} aria-hidden="true" className="shrink-0" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Go to…"
              aria-label="Go to"
              className="min-w-0 flex-1 bg-transparent text-sm text-[var(--fl-text-1)] outline-none placeholder:text-[var(--fl-text-3)]"
            />
          </label>
        </div>
        <nav aria-label="All sections" className="flex-1 overflow-y-auto px-2 pb-2">
          {q ? (
            matches.length > 0 ? (
              <div className="pt-2">{matches.map(link)}</div>
            ) : (
              <p className="px-2 py-6 text-center text-sm text-[var(--fl-text-2)]">
                No section called “{query.trim()}”.
              </p>
            )
          ) : (
            GROUPS.map((group) => {
              const items = NAV_ITEMS.filter((item) => item.group === group)
              if (items.length === 0) return null
              return (
                <div key={group}>
                  {group !== 'Settings' && (
                    <p className="px-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fl-text-3)]">
                      {group}
                    </p>
                  )}
                  {group === 'Settings' && (
                    <div className="mx-2 my-2 border-t border-[var(--fl-divider)]" />
                  )}
                  {items.map(link)}
                </div>
              )
            })
          )}
        </nav>
        <div className="border-t border-[var(--fl-border)] px-3 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          {footer}
        </div>
      </div>
    </div>
  )
}
